import { useEffect, useMemo, useState } from 'react';

import { Search, Filter } from 'lucide-react';

import { PromptSection } from '@/components/prompt-library/prompt-section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';
import {
  CATEGORIES,
  getAllPrompts,
  type Prompt,
} from '@/lib/prompt-lib-utils';

export default function PromptLibraryPage() {
  const { isMobile } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Scroll to top when page mounts
  useEffect(() => {
    requestAnimationFrame(() => {
      const mainContainer = document.querySelector('main');
      if (mainContainer) {
        mainContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    });
  }, []);

  const allPrompts = useMemo(() => getAllPrompts(), []);

  // Filter prompts based on search and category
  const filteredPrompts = useMemo(() => {
    let prompts = allPrompts;

    // Filter by search query
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      prompts = prompts.filter(
        (p) =>
          p.title.toLowerCase().includes(lowerQuery) ||
          p.description.toLowerCase().includes(lowerQuery)
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      prompts = prompts.filter((p) => p.category === selectedCategory);
    }

    // Group by category
    const grouped = new Map<string, Prompt[]>();
    for (const cat of CATEGORIES) {
      grouped.set(cat.id, []);
    }
    for (const prompt of prompts) {
      const existing = grouped.get(prompt.category) || [];
      existing.push(prompt);
      grouped.set(prompt.category, existing);
    }

    return grouped;
  }, [allPrompts, searchQuery, selectedCategory]);

  return (
    <div className="min-h-full">
      <div className={cn(
        "max-w-[1400px] mx-auto",
        isMobile ? "px-4 py-6" : "px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10"
      )}>
        {/* Header */}
        <div className={cn(isMobile ? "mb-5" : "mb-10")}>
          {isMobile ? (
            // Mobile: Stacked layout
            <>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Prompt Library
              </h1>
              <p className="text-muted-foreground text-sm mb-4">
                Explore optimized prompts for business and personal tasks.
              </p>
              {/* Search + Filter row */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 shrink-0"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </div>
              {/* Mobile category dropdown */}
              {showMobileFilters && (
                <div className="mt-3">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="All prompts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All prompts</SelectItem>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          ) : (
            // Desktop layout
            <>
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-2">
                    Prompt Library
                  </h1>
                  <p className="text-muted-foreground text-sm sm:text-base">
                    Explore optimized prompts for business and personal tasks
                  </p>
                </div>
                <div className="relative w-[240px] shrink-0 mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search prompts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                    selectedCategory === 'all'
                      ? 'bg-foreground/90 text-background border-foreground/90'
                      : 'bg-card text-muted-foreground border-border hover:text-foreground'
                  )}
                >
                  All
                </button>
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                      selectedCategory === cat.id
                        ? 'bg-foreground/90 text-background border-foreground/90'
                        : 'bg-card text-muted-foreground border-border hover:text-foreground'
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Prompt Sections */}
        {CATEGORIES.map((category) => {
          const prompts = filteredPrompts.get(category.id) || [];
          if (selectedCategory !== 'all' && selectedCategory !== category.id) {
            return null;
          }
          return (
            <PromptSection
              key={category.id}
              title={category.label}
              prompts={prompts}
              isMobile={isMobile}
            />
          );
        })}

        {/* Empty state */}
        {allPrompts.length === 0 && (
          <div className={cn("text-center", isMobile ? "py-12" : "py-20")}>
            <p className={cn("text-muted-foreground", isMobile && "text-sm")}>
              No prompts available yet.
            </p>
          </div>
        )}

        {/* No results */}
        {allPrompts.length > 0 &&
          Array.from(filteredPrompts.values()).every(
            (arr) => arr.length === 0
          ) && (
            <div className={cn("text-center", isMobile ? "py-12" : "py-20")}>
              <p className={cn("text-muted-foreground", isMobile && "text-sm")}>
                No prompts found matching your search.
              </p>
            </div>
          )}
      </div>
    </div>
  );
}
